import type { MultipleQueriesResponse, MultipleQueriesQuery } from '@algolia/client-search'

import { client } from './clients'

const DEFAULT_INDEX = 'popular'

const EXCLUDED_ATTRIBUTES = [
  'allSizes',
  'availableForSale',
  'createdAt',
  'colors',
  'currency',
  'description',
  'frequency',
  'graphqlId',
  'hasCompareAtPrice',
  'isStarred',
  'originalImageUrl',
  'price',
  'publishedAt',
  'sizes',
  'stockAvailable',
  'storefrontId',
  'type',
  'updatedAt',
]

type BaseFacet<ATTRIBUTE, VALUE> = {
  attribute: ATTRIBUTE
  value: VALUE
  excluded?: boolean
}

export type Facet =
  | BaseFacet<'tags', string[]>
  | BaseFacet<'sizes', string[]>
  | BaseFacet<'vendors', string[]>
  | BaseFacet<'brand', string[]>
  | BaseFacet<'colors', string[]>
  | BaseFacet<'hasCompareAtPrice', boolean>

export type Sorts = ['popular', 'mostRecent', 'ascendingPrice', 'descendingPrice', 'AtoZ', 'ZtoA']

export type SortIndex = Sorts[number]

type Query = {
  facets?: Facet[]
  hardFilters?: Facet[]
  fullTextSearch?: string
  page?: number
  hitsPerPage?: number
  sortIndex?: SortIndex
  priceRange?: { start?: number; end?: number }
  filters?: (string[] | string)[]
}

export type Product = {
  allColors?: string[]
  allSizes?: string[]
  availableForSale?: boolean
  brand?: string
  colors?: []
  compareAtPriceRange?: [number, number]
  createdAt?: string
  currency?: string
  description?: string
  graphqlId?: string
  handle?: string
  id?: number
  imageUrl1x?: string
  imageUrl2x?: string
  imageUrl3x?: string
  name?: string
  originalImageUrl?: string
  price?: number
  priceRange?: [number, number]
  publishedAt?: string
  sizes?: []
  stockAvailable?: number
  storefrontId?: string
  tags?: string[]
  type?: string
  updatedAt?: string
  vendor?: string
  objectID?: string
}

export type Response = MultipleQueriesResponse<Product>

export function search({
  sortIndex = DEFAULT_INDEX,
  fullTextSearch,
  priceRange = {},
  filters = [],
  page,
  hitsPerPage,
  hardFilters = [],
  facets = [],
}: Query): Promise<Response> {
  const sharedQueryBase: MultipleQueriesQuery = {
    indexName: sortIndex,
    query: fullTextSearch,
    params: {
      maxValuesPerFacet: 1000,
      facetingAfterDistinct: true,
      attributesToHighlight: [],
      numericFilters: priceRangeNumericFilter(priceRange.start, priceRange.end),
      filters: filters.map((group) => (Array.isArray(group) ? group.join(' OR ') : group)).join(' AND '),
    },
  }

  const mainQuery: MultipleQueriesQuery = {
    ...sharedQueryBase,
    params: {
      ...sharedQueryBase.params,
      attributesToRetrieve: ['*', ...EXCLUDED_ATTRIBUTES.map((attribute) => `-${attribute}`)],
      page: page ?? 0,
      hitsPerPage,
      facets: ['*'],
      facetFilters: [...hardFilters.flatMap(facetFilter), ...facets.map(facetFilter)],
    },
  }

  const facetsQueries: MultipleQueriesQuery[] = facets
    .filter((facet) => !facet.excluded)
    .map((mainFacet) => {
      return {
        ...sharedQueryBase,
        params: {
          ...sharedQueryBase.params,
          attributesToRetrieve: [],
          page: 0,
          hitsPerPage: 1,
          facets: [mainFacet.attribute],
          facetFilters: [
            ...hardFilters.flatMap(facetFilter),
            ...facets.filter((facet) => facet !== mainFacet).map(facetFilter),
          ],
        },
      }
    })

  const priceFacet: MultipleQueriesQuery = {
    ...mainQuery,
    params: { ...mainQuery.params, numericFilters: '', facets: ['price'] },
  }

  const queries: MultipleQueriesQuery[] = [mainQuery, ...facetsQueries, priceFacet]

  return client.multipleQueries<Product>(queries)
}

function facetFilter(facet: Facet): string[] {
  if (!Array.isArray(facet.value)) {
    return [`${facet.attribute}:${facet.value}`]
  }

  return facet.value.map((value) => `${facet.attribute}:${value}`)
}

function priceRangeNumericFilter(start?: number, end?: number): string {
  const formatedStart = formatPriceValue(start)
  const formatedEnd = formatPriceValue(end)
  if (formatedStart !== undefined && formatedEnd !== undefined) {
    return `price: ${formatedStart} TO ${formatedEnd}`
  }
  if (formatedStart !== undefined) {
    return `price>=${formatedStart}`
  }
  if (formatedEnd !== undefined) {
    return `price<=${formatedEnd}`
  }

  return ''
}

function formatPriceValue(value?: number): number | undefined {
  if (Number.isFinite(value)) {
    return value
  }
  return undefined
}
